import _ from 'lodash';
import { getIntl } from 'umi';

import env from '@/config/env';
import { toODataQuery } from '@/utils/core/data-transformation/transformers';
import { CoreEnums } from '@/utils/domain/constants';
import { isNullOrEmpty } from '@/utils/infrastructure';
import { getToken } from '@/utils/infrastructure/communication/request';
import CoreQueries from '@/utils/input/queries';
import HttpService from './http';

const { CustomListCommandType, CustomListCommandStringType } = CoreEnums;

export async function getAll({ route, ...params }, options = {}) {
  return HttpService.getInstance().request(`/${route}`, { ...params, ...options });
}

export async function getById({ id, route, ...params }, options = {}) {
  return HttpService.getInstance().request(`/${route}/${id}`, { ...params, ...options });
}

export async function create({ route, ...params }, options = {}) {
  return HttpService.getInstance().request(`/${route}`, { method: 'post', ...params, ...options });
}

export async function update({ route, ...params }, options = {}) {
  return HttpService.getInstance().request(`/${route}`, { method: 'put', ...params, ...options });
}

export async function updateById({ id, route, ...params }, options = {}) {
  return HttpService.getInstance().request(`/${route}/${id}`, {
    method: 'patch',
    ...params,
    ...options,
  });
}

export async function remove({ id, route }, options = {}) {
  return HttpService.getInstance().request(`/${route}/${id}`, { method: 'delete', ...options });
}

export async function activate({ id, route, isActive }, options = {}) {
  return HttpService.getInstance().request(`/${route}/activate/${id}/${isActive}`, {
    method: 'post',
    ...options,
  });
}

export async function getPerPaged({ route, ...params }, options = {}) {
  return HttpService.getInstance().request(`/${route}/paged`, { params, ...options });
}

export async function apiRoute({ route, ...params }, options = {}) {
  return HttpService.getInstance().request(`/${route}`, { ...params, ...options });
}

export async function bulkCommand({ command, observations, data = [] }, callback, options = {}) {
  const promises = data.map((item) => {
    /* The backend can issue commands as numeric enums (legacy) or kebab
     * strings (current). Index by both shapes so the bulk dispatcher
     * resolves the correct route regardless of the source.
     *
     * Mass-* commands are the canonical bulk variants of the per-row
     * commands; legacy single-instance keys (CancelRequest, VoidRequest,
     * ApproveRequest) still map for backwards compatibility. */
    const route = {
      [CustomListCommandType.MassRequestsApprove]: `approve/${item.ProcessInstance_Id}/${item.CurrentActionInstance_Id}`,
      [CustomListCommandType.MassRequestsReject]: `reject/${item.ProcessInstance_Id}/${item.CurrentActionInstance_Id}`,
      [CustomListCommandType.MassRequestsCancel]: `cancel/${item.ProcessInstance_Id}`,
      [CustomListCommandType.MassRequestsVoid]: `void/${item.ProcessInstance_Id}`,
      [CustomListCommandType.ApproveRequest]: `approve/${item.ProcessInstance_Id}/${item.CurrentActionInstance_Id}`,
      [CustomListCommandType.CancelRequest]: `cancel/${item.ProcessInstance_Id}`,
      [CustomListCommandType.VoidRequest]: `void/${item.ProcessInstance_Id}`,
      [CustomListCommandStringType.MassRequestsApprove]: `approve/${item.ProcessInstance_Id}/${item.CurrentActionInstance_Id}`,
      [CustomListCommandStringType.MassRequestsReject]: `reject/${item.ProcessInstance_Id}/${item.CurrentActionInstance_Id}`,
      [CustomListCommandStringType.MassRequestsCancel]: `cancel/${item.ProcessInstance_Id}`,
      [CustomListCommandStringType.MassRequestsVoid]: `void/${item.ProcessInstance_Id}`,
      [CustomListCommandStringType.ApproveRequest]: `approve/${item.ProcessInstance_Id}/${item.CurrentActionInstance_Id}`,
      [CustomListCommandStringType.CancelRequest]: `cancel/${item.ProcessInstance_Id}`,
      [CustomListCommandStringType.VoidRequest]: `void/${item.ProcessInstance_Id}`,
    }[command];

    return () => {
      return HttpService.getInstance().request(`/processEngine/${route}`, {
        data: { ...(!isNullOrEmpty(observations) && { observations }) },
        method: 'post',
        ...options,
      });
    };
  });

  return executeSequentially(promises, callback);
}

/**
 * Execute promises sequentially
 * @param {Array<Function>} promises - Array of functions that return promises
 * @param {Function} [callback]
 * @returns
 */
export function executeSequentially(promises, callback) {
  return promises.reduce(async (prev, task, index) => {
    try {
      const done = await prev;
      callback?.(+1, promises.length);
      return task(done);
    } catch (err) {
      if (err?.message) {
        const { formatMessage } = getIntl();
        if (!_.includes(err.message, formatMessage({ id: 'global.error.default' }))) {
          throw new Error(err);
        }
      }
      callback?.(+1, promises.length);
      return task(err);
    }
  }, Promise.resolve());
}

/**
 * Execute dynamic HTTP command
 * @param {Object} params - Command parameters
 * @param {string} params.route - API route
 * @param {string} params.httpMethod - HTTP method (get/post/put/delete)
 * @param {Object} params.data - Request data for POST
 * @param {Object} options - Request options
 */
export async function exec({ route, httpMethod, data = {}, ...params }, options = {}) {
  return HttpService.getInstance().request(`/${route}`, {
    method: _.toLower(httpMethod),
    ...(_.toLower(httpMethod) === 'post' && { data }),
    ...params,
    ...options,
  });
}

/**
 * Execute bulk action (activate/deactivate/delete) on multiple items
 * @param {Object} params - Bulk action parameters
 * @param {string} params.route - API route (e.g., 'shortenedUrl', 'user')
 * @param {number} params.bulkActionType - Bulk action type (BulkActionType.Activate, BulkActionType.Deactivate, etc.)
 * @param {Array} params.ids - Array of item IDs to process
 * @param {Object} options - Request options
 */
export async function bulkAction({ route, bulkActionType, ids = [] }, options = {}) {
  return HttpService.getInstance().request(`/${route}/bulkAction/${bulkActionType}`, {
    method: 'post',
    data: ids,
    ...options,
  });
}

/**
 * Complete process instance action with dynamic fragments
 * @param {Object} params - Completion parameters
 * @param {string} params.processInstanceId - Process instance ID
 * @param {string} params.actionInstanceId - Action instance ID
 * @param {string} params.formInstanceId - Form instance ID
 * @param {string} params.operationType - Operation type
 * @param {Object} params.data - Form data
 * @param {Object} options - Request options
 */
export async function complete({ data = {}, ...params }, options = {}) {
  const fragments = [
    params?.processInstanceId,
    params?.actionInstanceId,
    params?.formInstanceId,
    params?.operationType,
  ].filter(Boolean);
  return HttpService.getInstance().request(`/processEngine/continue/${fragments.join('/')}`, {
    method: 'post',
    data,
    ...options,
  });
}

/**
 * Query data with dynamic endpoint and OData transformation
 * @param {Object} params - Query parameters
 * @param {string} params.route - Data route
 * @param {string} params.httpMethod - HTTP method (get/post)
 * @param {string} params.processInstanceId - Process instance ID
 * @param {string} params.formId - Form ID
 * @param {string} params.tableId - Table ID
 * @param {string} params.actionInstanceId - Action instance ID
 * @param {string} params.query - CoreQueries reference
 * @param {Object} options - Request options
 */
export async function getData({ route, httpMethod = 'get', ...params }, options = {}) {
  const ids = ['processInstanceId', 'formId', 'tableId', 'actionInstanceId'];
  const fragments = ids.map((k) => params?.[k]).filter(Boolean);

  const method = _.toLower(httpMethod) ?? 'get';
  const endPoint = { get: 'data', post: 'export' }[method];
  const queryParams = params.query ? CoreQueries[params.query] : _.omit(params, ids);

  // HttpService will transform these params using toQuery, so we pass them as-is
  return HttpService.getInstance().request(
    `/processInstance/${endPoint}/${route}/${fragments.join('/')}`,
    {
      method,
      ...queryParams,
      ...options,
    },
  );
}

/**
 * Bind common API services to a namespace for request tracking
 * @param {string} namespace - Model namespace
 * @returns {Object} Bound service functions
 *
 * @example
 * // In model file
 * const api = bindServices('applications');
 *
 * // In effects
 * yield call(api.getAll, { route: 'items' });
 * yield call(api.getById, { id, route: 'items' });
 * yield call(api.create, { route: 'items', data });
 */
export function bindServices(namespace) {
  const bind = (fn) => (params, options = {}) => fn(params, { ...options, namespace });

  return {
    getAll: bind(getAll),
    getById: bind(getById),
    create: bind(create),
    update: bind(update),
    updateById: bind(updateById),
    remove: bind(remove),
    apiRoute: bind(apiRoute),
    activate: bind(activate),
    // Complex/custom functions
    exec: bind(exec),
    complete: bind(complete),
    getData: bind(getData),
    bulkAction: bind(bulkAction),
    bulkCommand: (params, callback) => bulkCommand(params, callback, { namespace }),
    executeSequentially: bind(executeSequentially),
  };
}

// ── Apps: methods that require native fetch (CORS / redirect handling) ──

export async function getAppPreview(appId, versionId, versionToken) {
  const authToken = getToken();
  const url = `${env.API_URL}/app/config/preview/${appId}/${versionId}?token=${encodeURIComponent(
    versionToken,
  )}`;
  const headers = { clientId: env.CLIENT_ID };

  if (authToken?.accessToken) {
    headers.Authorization = `Bearer ${authToken.accessToken}`;
  }

  const response = await fetch(url, { method: 'POST', headers, credentials: 'omit' });
  if (!response.ok) throw new Error(`Preview failed: ${response.status}`);
  return response.text();
}

export async function getAppRenderHtml(appId) {
  const url = `${env.ASSETS_URL}/app/${appId}/render`;
  const response = await fetch(url, { method: 'GET', credentials: 'omit' });
  if (!response.ok) {
    if (response.status === 404) {
      const err = new Error('NOT_PUBLISHED');
      err.code = 'NOT_PUBLISHED';
      err.status = 404;
      throw err;
    }
    const err = new Error(`Render fetch failed: ${response.status}`);
    err.status = response.status;
    throw err;
  }
  return response.text();
}

export async function getAppManifest(appId) {
  const url = `${env.ASSETS_URL}/app/${appId}/manifest`;
  const response = await fetch(url, { method: 'GET', credentials: 'omit' });
  if (!response.ok) return null;
  return response.json();
}
